import React from 'react';
import { createPortal } from 'react-dom';
// @ts-ignore - CSS Side Effect Import
import 'quill/dist/quill.snow.css';
import ImageDialog from './ImageDialog';
import ImageResizeDialog from './ImageResizeDialog';

let customBlotsRegistered = false;

interface IQuillEditorProps {
  value: string;
  onChange: (value: string) => void;
}

interface IQuillEditorState {
  quillLoaded: boolean;
  showImageDialog: boolean;
  showImageResizeDialog: boolean;
  resizeInitialWidth: string;
  resizeInitialHeight: string;
  resizeInitialIsPreview: boolean;
  resizeInitialAlt: string;
  isSourceMode: boolean;
  sourceHtml: string;
}

// Quill is loaded dynamically to avoid SSR issues and bundling Quill twice.
export default class QuillEditor extends React.Component<IQuillEditorProps, IQuillEditorState> {
  private containerRef = React.createRef<HTMLDivElement>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private quillInstance: any = null;
  private suppressNextChange = false;
  private pendingInsertIndex: number | null = null;
  private activeImageNode: HTMLImageElement | null = null;
  private sourceEditorDebounceTimer: number | null = null;
  // Steuerzeichen für Softbreak
  private readonly SOFTBREAK_CHAR = '\u2028';

  constructor(props: IQuillEditorProps) {
    super(props);
    this.state = {
      quillLoaded: false,
      showImageDialog: false,
      showImageResizeDialog: false,
      resizeInitialWidth: '',
      resizeInitialHeight: '',
      resizeInitialIsPreview: false,
      resizeInitialAlt: '',
      isSourceMode: false,
      sourceHtml: '',
    };
  }
  
  componentDidMount(): void {
    import('quill').then(({ default: Quill }) => {
      if (!this.containerRef.current || this.quillInstance) {
        return;
      }

      if (!customBlotsRegistered) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const BlockEmbed: any = Quill.import('blots/block/embed');

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        class DividerBlot extends (BlockEmbed as any) {
          static blotName = 'divider';
          static tagName = 'HR';
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        class PagebreakBlot extends (BlockEmbed as any) {
          static blotName = 'pagebreak';
          static tagName = 'P';
          static className = '__pagebreak';

          static create(value: unknown): HTMLElement {
            const node = super.create(value) as HTMLElement;
            node.innerHTML = '<!--pagebreak-->';
            return node;
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        class HighlightTextBlot extends (BlockEmbed as any) {
          static blotName = 'highlighttext';
          static tagName = 'DIV';
          static className = 'blockquote-border';

          static create(value: unknown): HTMLElement {
            const node = super.create(value) as HTMLElement;
            const rawText = typeof value === 'string' ? value : '';
            const escapedText = rawText
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;');
            node.innerHTML = `<div><div><p>${escapedText}</p></div></div>`;
            return node;
          }

          static value(node: HTMLElement): string {
            const paragraph = node.querySelector('p');
            return paragraph ? (paragraph.textContent || '') : '';
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        class PergamentPageBlot extends (BlockEmbed as any) {
          static blotName = 'pergamentpage';
          static tagName = 'DIV';
          static className = 'pergamentpage';

          static create(value: unknown): HTMLElement {
            const node = super.create(value) as HTMLElement;
            const rawHtml = typeof value === 'string' ? value : '';
            node.innerHTML = rawHtml;
            return node;
          }

          static value(node: HTMLElement): string {
            return node.innerHTML || '';
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        class PathGalleryBlot extends (BlockEmbed as any) {
          static blotName = 'pathgallery';
          static tagName = 'DIV';

          static matches(node: Node): boolean {
            return (
              node instanceof HTMLElement &&
              node.tagName.toLowerCase() === 'div' &&
              (node as HTMLElement).getAttribute('role') === 'path-gallery'
            );
          }

          static create(): HTMLElement {
            const node = document.createElement('div');
            node.setAttribute('role', 'path-gallery');
            return node;
          }

          static value(): boolean {
            return true;
          }
        }

        Quill.register(DividerBlot, true);
        Quill.register(PagebreakBlot, true);
        Quill.register(HighlightTextBlot, true);
        Quill.register(PergamentPageBlot, true);
        Quill.register(PathGalleryBlot, true);

        // Registriere einen einfachen Style-Attributor für CSS-Properties
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Parchment: any = Quill.import('parchment');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Style = new Parchment.Attributor('style', 'style', {
          scope: Parchment.Scope.INLINE
        });

        /*let config = {
          scope: Parchment.Scope.BLOCK,
        };

        let MClass = new Parchment.Attributor.Class('mark', 'dom-mark', config);
        Quill.register(MClass,true)*/

        Quill.register(Style, true);

        customBlotsRegistered = true;
      }

      this.quillInstance = new Quill(this.containerRef.current, {
        theme: 'snow',
        formats: [
          'header', 'bold', 'italic', 'underline', 
          'strike', 'list', 'link', 'image',
          'blockquote', 'code-block', 'divider', 'pagebreak', 'highlighttext', 'pergamentpage', 'pathgallery', 'style'],
        modules: {
          toolbar: {
            container: [
              [{ header: [1, 2, 3, false] }],
              ['bold', 'italic', 'underline', 'strike'],                      
              ['initial', 'softbreak'],
              ['clean'],
              [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link', 'image'],
              ['blockquote', 'highlighttext', 'pergamentpage'],
              ['hr', 'pagebreak'],
              ['pathgallery'],
              ['source']
            ],
            handlers: {
              initial: this.handleToolbarInitial,
              softbreak: this.handleToolbarSoftbreak,
              hr: this.handleToolbarHr,
              pagebreak: this.handleToolbarPagebreak,
              highlighttext: this.handleToolbarHighlightText,
              pergamentpage: this.handleToolbarPergamentPage,
              underline: this.handleToolbarUnderline,
              align: this.handleToolbarAlign,
              image: this.handleToolbarImage,
              pathgallery: this.handleToolbarPathGallery,
              source: this.handleToolbarSourceToggle,
            },
          },
          clipboard: {
            matchVisual: false,
          },
        },
      });

        
      //this.quillInstance.format('mark', 'MarkThisHere');
      
      this.decorateCustomToolbarButtons();

      // Registriere Selection-Change Listener um Button-States zu aktualisieren
      this.quillInstance.on('selection-change', (range: any) => {
        if (!this.state.showImageResizeDialog) {
          this.updateParaClassButtonStates();
        }
      });
      this.quillInstance.on('text-change', () => {
        // Ignoriere text-change Events im Source Mode
        if (this.state.isSourceMode) {
          return;
        }
        if (this.suppressNextChange) {
          this.suppressNextChange = false;
          return;
        }
        this.props.onChange(this.quillInstance.root.innerHTML as string);
      });

      // Registriere Editor-Root Click Handler für Bild-Auswahl
      const root = this.getEditorRoot();
      if (root) {
        root.addEventListener('click', this.handleEditorRootClick);
        root.addEventListener('keydown', this.handleEditorKeydown);
      }

      // Lade Inhalt nach einer kurzen Verzögerung um sicherzustellen, dass der Editor vollständig initialisiert ist
      if (this.props.value) {
        setTimeout(() => {
          if (this.quillInstance && this.containerRef.current) {
            const html = this.props.value;
            this.quillInstance.clipboard.dangerouslyPasteHTML(html);
            this.extractAndRestoreClasses(html);
          }
        }, 50);
      }

      this.setState({ quillLoaded: true });
    }).catch(() => {
      // Quill failed to load - degrade gracefully
    });
  }

  componentDidUpdate(prevProps: IQuillEditorProps): void {
    if (!this.quillInstance || prevProps.value === this.props.value) {
      return;
    }

    if (this.state.isSourceMode) {
      const nextHtml = this.props.value ?? '';
      const root = this.getEditorRoot();
      if (root && (root.textContent ?? '') !== nextHtml) {
        root.textContent = nextHtml;
      }
      if (this.state.sourceHtml !== nextHtml) {
        this.setState({ sourceHtml: nextHtml });
      }
      return;
    }

    const current: string = this.quillInstance.root.innerHTML;
    if (current !== this.props.value) {
      this.suppressNextChange = true;
      const nextHtml = this.props.value ?? '';
      this.quillInstance.setContents([]);
      this.quillInstance.clipboard.dangerouslyPasteHTML(nextHtml);
      this.extractAndRestoreClasses(nextHtml);
    }
  }

  componentWillUnmount(): void {
    if (this.quillInstance) {
      // Entferne text-change Listener
      this.quillInstance.off('text-change');
      
      // Entferne Event Handler vom Root-Element
      if (this.quillInstance.root) {
        this.quillInstance.root.removeEventListener('click', this.handleEditorRootClick);
        this.quillInstance.root.removeEventListener('keydown', this.handleEditorKeydown);
      }
    }

    // Cleanup für Source Editor Debounce
    if (this.sourceEditorDebounceTimer !== null) {
      window.clearTimeout(this.sourceEditorDebounceTimer);
      this.sourceEditorDebounceTimer = null;
    }

    this.clearImageSelection();
    this.setSourceEditorPresentation(false);
    this.quillInstance = null;
  }  

  private getEditorRoot = (): HTMLElement | null => {
    if (!this.quillInstance || !this.quillInstance.root) {
      return null;
    }
    return this.quillInstance.root as HTMLElement;
  };

  private applyImageAlignment = (imageNode: HTMLImageElement, alignValue: string): void => {
    let align = String(alignValue || '').toLowerCase().trim();
    if (align !== 'center' && align !== 'right') {
      align = 'left';
    }

    imageNode.setAttribute('data-align', align);
    
    // Prüfe ob Bild in einem Span Wrapper ist (typisch bei Quill)
    const parent = imageNode.parentElement;
    const isWrappedInSpan = parent && parent.tagName.toLowerCase() === 'span';
    
    // Stelle den Target Element fest (span wenn vorhanden, sonst img)
    const targetElement = isWrappedInSpan ? parent : imageNode;
    
    // Entferne alte Alignment Styles vom Target
    targetElement.style.removeProperty('float');
    targetElement.style.removeProperty('display');
    targetElement.style.removeProperty('text-align');
    targetElement.style.removeProperty('margin-left');
    targetElement.style.removeProperty('margin-right');
    
    // Stelle sicher dass Image selbst keine conflicting styles hat
    imageNode.style.removeProperty('float');
    imageNode.style.removeProperty('display');
    imageNode.style.removeProperty('text-align');
    imageNode.style.removeProperty('margin-left');
    imageNode.style.removeProperty('margin-right');

    // Wende Alignment auf Target an
    if (align === 'center') {
      // Center: Block display mit text-align center
      targetElement.style.display = 'block';
      targetElement.style.textAlign = 'center';
      imageNode.style.display = 'inline-block';
    } else if (align === 'right') {
      // Right: Float Right
      targetElement.style.float = 'right';
      targetElement.style.marginLeft = '12px';
    } else {
      // Left: Float Left
      targetElement.style.float = 'left';
      targetElement.style.marginRight = '12px';
    }
  };

  private getImageDimension = (imageNode: HTMLImageElement, key: 'width' | 'height'): number => {
    const attrValue = parseInt(String(imageNode.getAttribute(key) || ''), 10);
    if (Number.isFinite(attrValue) && attrValue > 0) {
      return attrValue;
    }

    const stylePattern = key === 'width'
      ? /(?:^|;)\s*width\s*:\s*(\d+)px\s*(?:;|$)/i
      : /(?:^|;)\s*height\s*:\s*(\d+)px\s*(?:;|$)/i;
    const styleMatch = (imageNode.getAttribute('style') || '').match(stylePattern);
    const styleValue = styleMatch ? parseInt(styleMatch[1], 10) : NaN;
    if (Number.isFinite(styleValue) && styleValue > 0) {
      return styleValue;
    }

    if (key === 'width' && imageNode.naturalWidth > 0) {
      return imageNode.clientWidth > 0 ? imageNode.clientWidth : imageNode.naturalWidth;
    }
    if (key === 'height' && imageNode.naturalHeight > 0) {
      return imageNode.clientHeight > 0 ? imageNode.clientHeight : imageNode.naturalHeight;
    }

    return 0;
  };

  private selectImageForEditing = (imageNode: HTMLImageElement): void => {
    if (this.activeImageNode && this.activeImageNode !== imageNode) {
      this.activeImageNode.classList.remove('wiki-image-selected');
    }

    this.activeImageNode = imageNode;
    this.activeImageNode.classList.add('wiki-image-selected');
  };

  private clearImageSelection = (): void => {
    if (this.activeImageNode) {
      this.activeImageNode.classList.remove('wiki-image-selected');
    }
    this.activeImageNode = null;
  };

  private openImageResizeDialog = (): boolean => {
    if (!this.activeImageNode || !this.quillInstance?.root?.contains(this.activeImageNode)) {
      return false;
    }

    this.setState({
      resizeInitialWidth: String(this.getImageDimension(this.activeImageNode, 'width') || ''),
      resizeInitialHeight: String(this.getImageDimension(this.activeImageNode, 'height') || ''),
      resizeInitialIsPreview: this.activeImageNode.classList.contains('preview'),
      resizeInitialAlt: this.activeImageNode.getAttribute('alt') || '',
      showImageResizeDialog: true,
    });

    return true;
  };

  private applyImageResize = (widthStr: string, heightStr: string, isPreview: boolean, alt: string): void => {
    if (!this.activeImageNode) {
      this.setState({ showImageResizeDialog: false });
      return;
    }

    const width = parseInt(String(widthStr || ''), 10);
    const height = parseInt(String(heightStr || ''), 10);

    if (Number.isFinite(width) && width > 0) {
      this.activeImageNode.setAttribute('width', String(width));
      this.activeImageNode.style.width = `${width}px`;
    } else {
      this.activeImageNode.removeAttribute('width');
      this.activeImageNode.style.removeProperty('width');
    }

    if (Number.isFinite(height) && height > 0) {
      this.activeImageNode.setAttribute('height', String(height));
      this.activeImageNode.style.height = `${height}px`;
    } else {
      this.activeImageNode.removeAttribute('height');
      this.activeImageNode.style.removeProperty('height');
    }

    if (isPreview) {
      this.activeImageNode.classList.add('preview');
    } else {
      this.activeImageNode.classList.remove('preview');
    }

    this.activeImageNode.setAttribute('alt', alt);

    this.quillInstance?.update('user');
    this.props.onChange(this.quillInstance.root.innerHTML as string);
    this.setState({ showImageResizeDialog: false });
  };

  private handleEditorRootClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (target && target.tagName && target.tagName.toLowerCase() === 'img') {
      this.selectImageForEditing(target as HTMLImageElement);
      return;
    }

    this.clearImageSelection();
  };

  private handleEditorKeydown = (event: KeyboardEvent): void => {
    // Delete oder Backspace Taste
    if (event.key === 'Delete' || event.key === 'Backspace') {
      // Nur wenn Bild ausgewählt ist und Source Mode nicht aktiv
      if (this.activeImageNode && !this.state.isSourceMode && this.quillInstance?.root?.contains(this.activeImageNode)) {
        event.preventDefault();
        
        // Entferne das Bild aus dem DOM
        this.activeImageNode.remove();
        this.clearImageSelection();
        
        // Trigger onChange für die Editor-Aktualisierung
        this.quillInstance.update('user');
        this.props.onChange(this.quillInstance.root.innerHTML as string);
      }
    }
  };

  private handleToolbarAlign = (value: string): void => {
    if (!this.quillInstance) {
      return;
    }

    if (this.activeImageNode && this.quillInstance.root.contains(this.activeImageNode)) {
      let imageAlignValue = String(value || '').toLowerCase().trim();
      if (imageAlignValue === 'justify') {
        imageAlignValue = 'left';
      }

      this.applyImageAlignment(this.activeImageNode, imageAlignValue || 'left');
      this.quillInstance.update('user');
      this.props.onChange(this.quillInstance.root.innerHTML as string);
      return;
    }

    if (this.state.isSourceMode) {
      return;
    }

    const root = this.getEditorRoot();
    if (!root) {
      return;
    }

    const range = this.quillInstance.getSelection(true);
    if (!range) {
      return;
    }

    const [startLine] = this.quillInstance.getLine(range.index) as [any, number];
    const endPos = range.index + (range.length > 0 ? range.length - 1 : 0);
    const [endLine] = this.quillInstance.getLine(endPos) as [any, number];

    if (!startLine || !endLine) {
      return;
    }

    const startIdx = this.quillInstance.getIndex(startLine) as number;
    const endLineIdx = this.quillInstance.getIndex(endLine) as number;
    const spanLength = endLineIdx + (endLine.length() as number) - startIdx;

    const alignValue = String(value || '').trim();
    const lines: any[] = this.quillInstance.getLines(startIdx, spanLength);

    lines.forEach((line: any) => {
      const node = line.domNode as HTMLElement | undefined;
      if (!node || !root.contains(node)) {
        return;
      }
      if (alignValue) {
        node.style.textAlign = alignValue;
      } else {
        node.style.removeProperty('text-align');
      }
    });

    this.quillInstance.update('user');
    this.props.onChange(this.quillInstance.root.innerHTML as string);
  };

  private handleToolbarUnderline = (value: unknown): void => {
    if (this.state.isSourceMode || !this.quillInstance) {
      return;
    }

    const range = this.quillInstance.getSelection(true);
    if (!range) {
      return;
    }

    const root = this.getEditorRoot();
    if (!root) {
      return;
    }

    const lineResult = this.quillInstance.getLine(Math.max(0, range.index));
    const lineNode = lineResult && lineResult[0] && lineResult[0].domNode
      ? (lineResult[0].domNode as Node)
      : null;
    const lineElement = lineNode instanceof HTMLElement ? lineNode : lineNode?.parentElement ?? null;
    const headingElement = lineElement?.closest('h1, h2, h3, h4, h5, h6') as HTMLHeadingElement | null;

    if (headingElement && root.contains(headingElement)) {
      headingElement.classList.add('titel-header-bottom');
      this.quillInstance.update('user');
      this.props.onChange(this.quillInstance.root.innerHTML as string);
      return;
    }

    const underlineValue = typeof value === 'boolean'
      ? value
      : !Boolean(this.quillInstance.getFormat(range).underline);
    this.quillInstance.format('underline', underlineValue, 'user');
  };

  private handleToolbarImage = (): void => {
    if (this.state.isSourceMode || !this.quillInstance) {
      return;
    }

    if (this.openImageResizeDialog()) {
      return;
    }

    const selection = this.quillInstance.getSelection(true);
    this.pendingInsertIndex = selection ? selection.index : this.quillInstance.getLength();
    this.setState({ showImageDialog: true });
  };

  private isImageUrl = (url: string): boolean => {
    return /\.(jpe?g|png|gif|webp|svg|bmp)(\?.*)?$/i.test(url);
  };

  private buildLinkLabel = (url: string): string => {
    const cleanUrl = String(url || '').split('?')[0];
    const fileName = cleanUrl.split('/').pop() || 'Datei oeffnen';
    try {
      return decodeURIComponent(fileName);
    } catch {
      return fileName;
    }
  };

  private handleDialogSelect = (url: string): void => {
    if (!this.quillInstance) {
      this.setState({ showImageDialog: false });
      return;
    }

    const insertIndex = this.pendingInsertIndex ?? this.quillInstance.getLength();

    if (this.isImageUrl(url)) {
      this.quillInstance.insertEmbed(insertIndex, 'image', url, 'user');
      this.quillInstance.insertText(insertIndex + 1, '\n', 'user');
      this.quillInstance.setSelection(insertIndex + 2, 0, 'silent');
    } else {
      const label = this.buildLinkLabel(url);
      this.quillInstance.insertText(insertIndex, label, 'link', url, 'user');
      this.quillInstance.insertText(insertIndex + label.length, '\n', 'user');
      this.quillInstance.setSelection(insertIndex + label.length + 1, 0, 'silent');
    }

    this.pendingInsertIndex = null;
    this.props.onChange(this.quillInstance.root.innerHTML as string);
    this.setState({ showImageDialog: false });
  };

  private handleDialogCancel = (): void => {
    this.pendingInsertIndex = null;
    this.setState({ showImageDialog: false });
  };

  private decorateCustomToolbarButtons = (): void => {
    if (!this.quillInstance) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toolbarModule: any = this.quillInstance.getModule('toolbar');
    const toolbar = toolbarModule?.container as HTMLElement | undefined;
    if (!toolbar) {
      return;
    }

    const highlightButton = toolbar.querySelector('button.ql-highlighttext') as HTMLButtonElement | null;
    if (highlightButton) {
      highlightButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2048 2048"><path class="ql-fill" d="M0 128h2048v1408H731l-475 475v-475H0V128zm1920 1280V256H128v1152h256v293l293-293h1243z"></path></svg>';
      highlightButton.title = 'Highlighttext einfuegen';
      highlightButton.setAttribute('aria-label', 'Highlighttext einfuegen');
    }

    const pergamentButton = toolbar.querySelector('button.ql-pergamentpage') as HTMLButtonElement | null;
    if (pergamentButton) {
      pergamentButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2048 2048"><path class="ql-fill" d="M896 1280q0 82-33 156t-95 130v458l-256-128-256 128v-458q-61-55-94-129t-34-157q0-79 30-149t82-122 122-83 150-30q79 0 149 30t122 83 82 122 31 149zm-384 256q53 0 99-20t82-55 55-81 20-100q0-53-20-99t-55-82-81-55-100-20q-53 0-99 20t-82 55-55 81-20 100q0 53 20 99t55 82 81 55 100 20zm128 106q-61 22-123 22-70 0-133-22v174l128-64 128 64v-174zM1920 549v1499H896v-128h896V640h-512V128H384v624q-33 8-65 20t-63 28V0h1115l549 549zm-219-37l-293-293v293h293z"></path></svg>';
      pergamentButton.title = 'Pergament-Seite einfügen';
      pergamentButton.setAttribute('aria-label', 'Pergament-Seite einfügen');
    }

    const hrButton = toolbar.querySelector('button.ql-hr') as HTMLButtonElement | null;
    if (hrButton) {
      hrButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2048 2048"><path class="ql-fill" d="M0 896h1920v128H0V896z"></path></svg>';
      hrButton.title = 'Linie einfügen';
      hrButton.setAttribute('aria-label', 'Linie einfügen');
    }

    const pagebreakButton = toolbar.querySelector('button.ql-pagebreak') as HTMLButtonElement | null;
    if (pagebreakButton) {
      pagebreakButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2048 2048"><path class="ql-fill" d="M1664 1664h256v128h-256v256h-128v-256h-256v-128h256v-256h128v256zM384 128v1536h768v128H256V0h859l549 549v731h-128V640h-512V128H384zm768 91v293h293l-293-293z"></path></svg>';
      pagebreakButton.title = 'Seitenumbruch einfügen';
      pagebreakButton.setAttribute('aria-label', 'Seitenumbruch einfügen');
    }

    const pathGalleryButton = toolbar.querySelector('button.ql-pathgallery') as HTMLButtonElement | null;
    if (pathGalleryButton) {
      pathGalleryButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2048 2048"><path class="ql-fill" d="M0 0h896v896H0V0zm128 768h640V128H128v640zm896-768h1024v896H1024V0zm128 768h768V128h-768v640zM0 1024h896v1024H0V1024zm128 896h640v-768H128v768zm896-896h1024v1024H1024V1024zm128 896h768v-768h-768v768z"></path></svg>';
      pathGalleryButton.title = 'Galerie-Platzhalter einfügen';
      pathGalleryButton.setAttribute('aria-label', 'Galerie-Platzhalter einfügen');
    }

    const sourceButton = toolbar.querySelector('button.ql-source') as HTMLButtonElement | null;
    if (sourceButton) {
      sourceButton.innerHTML = '&lt;/&gt;';
      sourceButton.title = 'HTML Quellcode umschalten';
      sourceButton.setAttribute('aria-label', 'HTML Quellcode umschalten');
      sourceButton.classList.toggle('ql-active', this.state.isSourceMode);
    }

    const initialButton = toolbar.querySelector('button.ql-initial') as HTMLButtonElement | null;
    if (initialButton) {
      initialButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2048 2048"><path class="ql-fill" d="M384 0h1408v2048H384q-53 0-99-20t-82-55-55-81-20-100V256q0-53 20-99t55-82 81-55T384 0zm1280 1920v-256H384q-27 0-50 10t-40 27-28 41-10 50q0 27 10 50t27 40 41 28 50 10h1280zm0-384V128H384q-27 0-50 10t-40 27-28 41-10 50v1314q60-34 128-34h1280zm-768-256V640h128v640H896zm0-768V384h128v128H896z"></path></svg>';
      initialButton.title = 'Initial-Absatz';
      initialButton.setAttribute('aria-label', 'Initial-Absatz');
    }

    const softbreakButton = toolbar.querySelector('button.ql-softbreak') as HTMLButtonElement | null;
    if (softbreakButton) {
      softbreakButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2048 2048"><path class="ql-fill" d="M1792 1152H475l466 467-90 90-621-621 621-621 90 90-466 467h1189V384h128v768z"></path></svg>';
      softbreakButton.title = 'Softbreak-Absatz';
      softbreakButton.setAttribute('aria-label', 'Softbreak-Absatz');
    }

    // Button states für 'initial' und 'softbreak' werden durch selection-change updatet
    this.updateParaClassButtonStates();
  };

  /*private getCurrentBlockElement = (): HTMLElement | null => {
    const paragraph = this.getCurrentParagraphElement();
    if (paragraph) {
      return paragraph;
    }

    const root = this.getEditorRoot();
    if (!root) {
      return null;
    }

    const nativeSelection = window.getSelection();
    const anchorNode = nativeSelection?.anchorNode ?? null;
    const anchorElement = anchorNode instanceof HTMLElement ? anchorNode : anchorNode?.parentElement ?? null;
    const blockElement = anchorElement?.closest('p, blockquote, li, div');
    return blockElement && root.contains(blockElement) ? (blockElement as HTMLElement) : null;
  };

  private escapeHtml = (value: string): string => {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };*/

  /*private insertHtmlAfterCurrentBlock = (html: string): void => {
    if (this.state.isSourceMode || !this.quillInstance) {
      return;
    }

    const root = this.getEditorRoot();
    if (!root) {
      return;
    }

    this.quillInstance.focus();

    const template = document.createElement('template');
    template.innerHTML = html;
    const insertedNodes = Array.from(template.content.childNodes);
    if (insertedNodes.length === 0) {
      return;
    }

    const block = this.getCurrentBlockElement();
    if (block && block.parentNode) {
      block.parentNode.insertBefore(template.content, block.nextSibling);
    } else {
      root.appendChild(template.content);
    }

    const focusTarget = insertedNodes.find((node) => node instanceof HTMLElement && node.tagName.toLowerCase() === 'p') as HTMLElement | undefined;
    if (focusTarget) {
      const range = document.createRange();
      range.selectNodeContents(focusTarget);
      range.collapse(true);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }

    this.quillInstance.update('user');
    this.props.onChange(root.innerHTML as string);
  };

  private replaceSelectionWithHtml = (html: string): void => {
    if (this.state.isSourceMode || !this.quillInstance) {
      return;
    }

    this.quillInstance.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    const root = this.getEditorRoot();
    if (!root || !root.contains(range.commonAncestorContainer)) {
      return;
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    const insertedNodes = Array.from(template.content.childNodes);

    // This is the crucial fix: extract the content BEFORE deleting
    const content = template.content.cloneNode(true) as DocumentFragment;
    
    // Now delete the range
    range.deleteContents();
    
    // And insert the content
    range.insertNode(content);

    const lastInsertedNode = insertedNodes[insertedNodes.length - 1] ?? null;
    if (lastInsertedNode) {
      const nextRange = document.createRange();
      nextRange.setStartAfter(lastInsertedNode);
      nextRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(nextRange);
    }

    this.quillInstance.update('user');
    this.props.onChange(root.innerHTML as string);
  };*/

  private handleToolbarHr = (): void => {
    if (this.state.isSourceMode || !this.quillInstance) {
      return;
    }

    const range = this.quillInstance.getSelection(true);
    const index = range ? range.index : this.quillInstance.getLength();
    this.quillInstance.insertEmbed(index, 'divider', true, 'user');
    //this.quillInstance.insertText(index + 1, '\n', 'user');
    this.quillInstance.setSelection(index + 2, 0, 'silent');
    this.props.onChange(this.quillInstance.root.innerHTML as string);
  };

  private handleToolbarPagebreak = (): void => {
    if (this.state.isSourceMode || !this.quillInstance) {
      return;
    }

    const range = this.quillInstance.getSelection(true);
    const index = range ? range.index : this.quillInstance.getLength();
    this.quillInstance.insertEmbed(index, 'pagebreak', true, 'user');
    //this.quillInstance.insertText(index + 1, '\n', 'user');
    this.quillInstance.setSelection(index + 2, 0, 'silent');
    this.props.onChange(this.quillInstance.root.innerHTML as string);
  };

  private getCurrentParagraph = (): HTMLElement | null => {
    if (!this.quillInstance) {
      return null;
    }

    const range = this.quillInstance.getSelection(true);
    if (!range) {
      return null;
    }

    const leafResult = this.quillInstance.getLeaf(Math.max(0, range.index));
    const leafNode = leafResult && leafResult[0] && leafResult[0].domNode ? (leafResult[0].domNode as Node) : null;
    const leafElement = leafNode instanceof HTMLElement ? leafNode : leafNode?.parentElement ?? null;
    return leafElement?.closest('p, li, blockquote, div') as HTMLElement | null;
  };

  private handleToolbarInitial = (): void => {
    if (this.state.isSourceMode || !this.quillInstance) {
      return;
    }

    this.toggleParagraphClass('initial');
  };

  private handleToolbarSoftbreak = (): void => {
    if (this.state.isSourceMode || !this.quillInstance) {
      return;
    }

    this.toggleParagraphClass('softbreak');
  };

  private toggleParagraphClass = (classValue: string): void => {
    if (!this.quillInstance) {
      return;
    }

    const range = this.quillInstance.getSelection(true);
    if (!range) {
      return;
    }

    // Finde den aktuellen Paragraph über das Blot-System
    const leafResult = this.quillInstance.getLeaf(Math.max(0, range.index));
    const leafNode = leafResult && leafResult[0] && leafResult[0].domNode ? (leafResult[0].domNode as Node) : null;
    const leafElement = leafNode instanceof HTMLElement ? leafNode : leafNode?.parentElement ?? null;
    const paragraph = leafElement?.closest('p, li, blockquote, div') as HTMLElement | null;

    if (!paragraph || !this.quillInstance.root.contains(paragraph)) {
      return;
    }

    // Prüfe aktuelles Format über das DOM-Element
    const hasClass = paragraph.classList.contains(classValue);

    if (hasClass) {
      paragraph.classList.remove(classValue);
    } else {
      paragraph.classList.add(classValue);
    }

    // Aktualisiere Quill (ohne UPDATE um text-change zu vermeiden)
    this.props.onChange(this.quillInstance.root.innerHTML as string);
    this.updateParaClassButtonStates();
  };

  private updateParaClassButtonStates = (): void => {
    if (!this.quillInstance) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toolbarModule: any = this.quillInstance.getModule('toolbar');
    const toolbar = toolbarModule?.container as HTMLElement | undefined;
    if (!toolbar) {
      return;
    }

    const range = this.quillInstance.getSelection();
    let isInitialActive = false;
    let isSoftbreakActive = false;

    if (range) {
      const leafResult = this.quillInstance.getLeaf(Math.max(0, range.index));
      const leafNode = leafResult && leafResult[0] && leafResult[0].domNode ? (leafResult[0].domNode as Node) : null;
      const leafElement = leafNode instanceof HTMLElement ? leafNode : leafNode?.parentElement ?? null;
      const paragraph = leafElement?.closest('p, li, blockquote, div') as HTMLElement | null;

      if (paragraph) {
        isInitialActive = paragraph.classList.contains('initial');
        isSoftbreakActive = paragraph.classList.contains('softbreak');
      }
    }

    // Aktualisiere Initial Button
    const initialButton = toolbar.querySelector('button.ql-initial') as HTMLButtonElement | null;
    if (initialButton) {
      if (isInitialActive) {
        initialButton.classList.add('ql-active');
      } else {
        initialButton.classList.remove('ql-active');
      }
    }

    // Aktualisiere Softbreak Button
    const softbreakButton = toolbar.querySelector('button.ql-softbreak') as HTMLButtonElement | null;
    if (softbreakButton) {
      if (isSoftbreakActive) {
        softbreakButton.classList.add('ql-active');
      } else {
        softbreakButton.classList.remove('ql-active');
      }
    }
  };

  private extractAndRestoreClasses = (html: string): void => {
    if (!this.quillInstance) {
      return;
    }

    const root = this.getEditorRoot();
    if (!root) {
      return;
    }

    // Erstelle ein temporäres Element um das HTML zu parsen
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Extrahiere alle Elemente mit initial oder softbreak Klassen
    const classedElements = temp.querySelectorAll('[class*="initial"], [class*="softbreak"], [class*="preview"]');
    
    // Erstelle eine Map von Text-Content zu Klassen
    const classMap = new Map<string, string>();
    classedElements.forEach((el: Element) => {
      const text = el.textContent || '';
      const classes = (el.getAttribute('class') || '').split(/\s+/).filter(c => ['initial', 'softbreak'].includes(c));
      // preview-Klasse an img-Tags separat tracken (img hat keinen textContent)
      if (el.tagName.toLowerCase() === 'img' && (el.getAttribute('class') || '').split(/\s+/).includes('preview')) {
        const src = (el as HTMLImageElement).getAttribute('src') || '';
        if (src) {
          classMap.set('__img__' + src, 'preview');
        }
      } else if (classes.length > 0 && text) {
        classMap.set(text.substring(0, 50), classes.join(' '));
      }
    });

    // Extrahiere Bild-Alignments aus HTML (data-align wird von Quills Sanitizer entfernt,
    // deshalb hier VOR dem Paste auslesen und per src-Key wiederherstellen).
    // Fallback: float-Style auf <img> selbst oder auf dem Parent-<span>/<div> (Quill-Wrapper).
    const imgAlignMap = new Map<string, string>();
    temp.querySelectorAll('img').forEach((el: Element) => {
      const img = el as HTMLImageElement;
      const src = img.getAttribute('src') || '';
      if (!src) return;

      // 1. data-align direkt auf dem img-Tag (gesetzt von applyImageAlignment)
      const dataAlign = img.getAttribute('data-align') || '';
      if (dataAlign) {
        imgAlignMap.set(src, dataAlign);
        return;
      }

      // 2. float-Style auf dem img selbst
      const imgFloat = img.style.float;
      if (imgFloat === 'left' || imgFloat === 'right') {
        imgAlignMap.set(src, imgFloat);
        return;
      }

      // 3. float-Style auf dem direkten Parent-Element (z.B. Quill-<span>-Wrapper)
      const parent = img.parentElement;
      if (parent && ['span', 'div', 'figure'].includes(parent.tagName.toLowerCase())) {
        const parentFloat = parent.style.float;
        if (parentFloat === 'left' || parentFloat === 'right') {
          imgAlignMap.set(src, parentFloat);
        }
      }
    });

    // Extrahiere alle Elemente mit text-align Styles
    const alignedElements = temp.querySelectorAll('[style*="text-align"]');
    const alignMap = new Map<string, string>();
    alignedElements.forEach((el: Element) => {
      const text = el.textContent || '';
      const styleAttr = (el as HTMLElement).style.textAlign || '';
      if (styleAttr && text) {
        alignMap.set(text.substring(0, 50), styleAttr);
      }
    });

    // Wende die Klassen, Styles und Bild-Alignments im aktuellen Editor wieder an
    if (classMap.size > 0 || alignMap.size > 0 || imgAlignMap.size > 0) {
      setTimeout(() => {
        const paragraphs = root.querySelectorAll('p, li, blockquote, div');
        paragraphs.forEach((p: Element) => {
          const pText = (p.textContent || '').substring(0, 50);
          if (classMap.has(pText)) {
            const classes = classMap.get(pText) || '';
            if (classes) {
              const existing = (p.getAttribute('class') || '').split(/\s+/).filter(c => !['initial', 'softbreak'].includes(c));
              existing.push(...classes.split(/\s+/));
              p.setAttribute('class', existing.join(' '));
            }
          }
          if (alignMap.has(pText)) {
            (p as HTMLElement).style.textAlign = alignMap.get(pText) || '';
          }
        });
        // preview-Klasse und Bild-Alignment an img-Tags wiederherstellen
        const imgs = root.querySelectorAll('img');
        imgs.forEach((el: Element) => {
          const img = el as HTMLImageElement;
          const src = img.getAttribute('src') || '';
          if (src && classMap.has('__img__' + src)) {
            img.classList.add('preview');
          }
          if (src && imgAlignMap.has(src)) {
            this.applyImageAlignment(img, imgAlignMap.get(src) as string);
          }
        });
      }, 10);
    }
  };

  private handleToolbarParaClass = (value: string): void => {
    if (this.state.isSourceMode || !this.quillInstance || !value) {
      return;
    }

    const range = this.quillInstance.getSelection(true);
    if (!range) {
      return;
    }

    // Finde den aktuellen Block/Paragraph
    const leafResult = this.quillInstance.getLeaf(Math.max(0, range.index));
    const leafNode = leafResult && leafResult[0] && leafResult[0].domNode ? (leafResult[0].domNode as Node) : null;
    const leafElement = leafNode instanceof HTMLElement ? leafNode : leafNode?.parentElement ?? null;
    const paragraph = leafElement?.closest('p, li, blockquote, div') as HTMLElement | null;

    if (!paragraph || !this.quillInstance.root.contains(paragraph)) {
      return;
    }

    // Extrahiere aktuelle Klassen
    const currentClasses = (paragraph.getAttribute('class') || '').split(/\s+/).filter(c => c);
    
    // Entferne bereits existierende paraClass Werte
    const filteredClasses = currentClasses.filter(c => !['initial', 'softbreak'].includes(c));

    if (!value || value === '') {
      // Entferne alle paraClass Klassen
      if (filteredClasses.length > 0) {
        paragraph.setAttribute('class', filteredClasses.join(' '));
      } else {
        paragraph.removeAttribute('class');
      }
    } else {
      // Füge die neue Klasse hinzu
      filteredClasses.push(value);
      paragraph.setAttribute('class', filteredClasses.join(' '));
    }

    this.quillInstance.update('user');
    this.props.onChange(this.quillInstance.root.innerHTML as string);
  };

  private handleToolbarHighlightText = (): void => {
    if (this.state.isSourceMode || !this.quillInstance) {
      return;
    }

    const range = this.quillInstance.getSelection(true);
    if (!range || range.length <= 0) {
      return;
    }

    const selectedText = this.quillInstance.getText(range.index, range.length);
    const trimmedText = String(selectedText || '').trim();
    if (trimmedText === '') {
      return;
    }

    this.quillInstance.deleteText(range.index, range.length, 'user');
    this.quillInstance.insertEmbed(range.index, 'highlighttext', trimmedText, 'user');
    this.quillInstance.setSelection(range.index + 1, 0, 'silent');
    this.props.onChange(this.quillInstance.root.innerHTML as string);
  };

  private handleToolbarPathGallery = (): void => {
    if (this.state.isSourceMode || !this.quillInstance) {
      return;
    }
    const range = this.quillInstance.getSelection(true);
    const index = range ? range.index : this.quillInstance.getLength();
    this.quillInstance.insertEmbed(index, 'pathgallery', true, 'user');
    this.quillInstance.setSelection(index + 2, 0, 'silent');
    this.props.onChange(this.quillInstance.root.innerHTML as string);
  };

  private handleToolbarPergamentPage = (): void => {
    if (this.state.isSourceMode || !this.quillInstance) {
      return;
    }

    const range = this.quillInstance.getSelection(true);
    if (!range) {
      return;
    }

    const root = this.getEditorRoot();
    if (!root) {
      return;
    }

    // ── Unwrap: Cursor liegt innerhalb eines div.pergamentpage ────────────────
    const leafResult = this.quillInstance.getLeaf(Math.max(0, range.index)) as [any, number];
    const leafNode = leafResult && leafResult[0] && leafResult[0].domNode ? (leafResult[0].domNode as Node) : null;
    const leafElement = leafNode instanceof HTMLElement ? leafNode : leafNode?.parentElement ?? null;
    const pergWrapper = leafElement?.closest('div.pergamentpage') as HTMLElement | null;

    if (pergWrapper && root.contains(pergWrapper)) {
      // Alle Kind-Nodes vor dem Wrapper einfügen und Wrapper entfernen
      const parent = pergWrapper.parentNode;
      if (parent) {
        while (pergWrapper.firstChild) {
          parent.insertBefore(pergWrapper.firstChild, pergWrapper);
        }
        parent.removeChild(pergWrapper);
      }
      this.quillInstance.update('user');
      this.props.onChange(this.quillInstance.root.innerHTML as string);
      return;
    }

    // ── Wrap: Markierten Bereich in div.pergamentpage einschließen ────────────
    // Vollständige Zeilen ermitteln, die die Selektion abdecken
    const [startLine] = this.quillInstance.getLine(range.index) as [any, number];
    const endPos = range.index + (range.length > 0 ? range.length - 1 : 0);
    const [endLine] = this.quillInstance.getLine(endPos) as [any, number];

    if (!startLine || !endLine) {
      return;
    }

    const startIdx = this.quillInstance.getIndex(startLine) as number;
    const endLineIdx = this.quillInstance.getIndex(endLine) as number;
    // line.length() beinhaltet das abschließende \n
    const spanLength = endLineIdx + (endLine.length() as number) - startIdx;

    // Alle Zeilen im Bereich sammeln
    const lines: any[] = this.quillInstance.getLines(startIdx, spanLength);
    const domNodes: HTMLElement[] = lines
      .map((l: any) => l.domNode as HTMLElement)
      .filter((n: HTMLElement) => n && root.contains(n));

    if (domNodes.length === 0) {
      return;
    }

    const innerHtml = domNodes.map(n => n.outerHTML).join('');

    // Wrapper-Div erzeugen und vor dem ersten Node einfügen
    const wrapper = document.createElement('div');
    wrapper.className = 'pergamentpage';
    wrapper.innerHTML = innerHtml;

    const firstNode = domNodes[0];
    if (firstNode.parentNode) {
      firstNode.parentNode.insertBefore(wrapper, firstNode);
    }

    // Originale Nodes entfernen
    domNodes.forEach(n => n.parentNode?.removeChild(n));

    // Quill-Modell und Callback aktualisieren
    this.quillInstance.update('user');
    this.props.onChange(this.quillInstance.root.innerHTML as string);
  };

  private setSourceEditorPresentation = (enabled: boolean, sourceText = ''): void => {
    const root = this.getEditorRoot();
    if (!root) {
      return;
    }

    root.removeEventListener('input', this.handleSourceEditorInput);

    if (enabled) {
      root.classList.add('source-mode');
      // Suppress text-change Events wenn wir den Content setzen
      this.suppressNextChange = true;
      root.textContent = sourceText;
      root.style.whiteSpace = 'pre-wrap';
      root.style.fontFamily = 'Consolas, "Courier New", monospace';
      root.style.fontSize = '13px';
      root.style.lineHeight = '1.4';
      root.style.tabSize = '2';
      root.setAttribute('contenteditable', 'true');
      root.addEventListener('input', this.handleSourceEditorInput);
      root.focus();
      return;
    }

    root.classList.remove('source-mode');
    root.style.whiteSpace = '';
    root.style.fontFamily = '';
    root.style.fontSize = '';
    root.style.lineHeight = '';
    root.style.tabSize = '';
  };

  private handleSourceEditorInput = (): void => {
    if (!this.state.isSourceMode) {
      return;
    }

    const root = this.getEditorRoot();
    if (!root) {
      return;
    }

    // Clear existing debounce timer
    if (this.sourceEditorDebounceTimer !== null) {
      window.clearTimeout(this.sourceEditorDebounceTimer);
    }

    // Debounce: Nur nach 300ms ohne Input einen Update machen
    this.sourceEditorDebounceTimer = window.setTimeout(() => {
      const nextHtml = root.textContent ?? '';
      if (nextHtml !== this.state.sourceHtml) {
        this.setState({ sourceHtml: nextHtml });
      }
      this.props.onChange(nextHtml);
      this.sourceEditorDebounceTimer = null;
    }, 300);
  };

  private handleToolbarSourceToggle = (): void => {
    if (!this.quillInstance) {
      return;
    }

    if (this.state.isSourceMode) {
      // Wir gehen AUS dem Source Mode zurück
      const html = this.state.sourceHtml;

      this.setSourceEditorPresentation(false);
      this.quillInstance.enable(true);
      this.quillInstance.setContents([]);

      // Nutze requestAnimationFrame für verzögertes Einfügen, um UI nicht zu blockieren
      requestAnimationFrame(() => {
        // Suppress text-change Event für das Einfügen des HTML
        this.suppressNextChange = true;
        this.quillInstance.clipboard.dangerouslyPasteHTML(html);
        this.extractAndRestoreClasses(html);
        this.setState({ isSourceMode: false }, () => {
          this.decorateCustomToolbarButtons();
          this.props.onChange(this.quillInstance.root.innerHTML as string);
        });
      });
      return;
    }

    // Wir gehen IN den Source Mode
    const currentHtml = this.quillInstance.root.innerHTML as string;
    this.clearImageSelection();
    this.quillInstance.enable(false);

    // Nutze requestAnimationFrame für verzögerte Präsentation, um UI nicht zu blockieren
    requestAnimationFrame(() => {
      this.setState({ isSourceMode: true, sourceHtml: currentHtml }, () => {
        this.setSourceEditorPresentation(true, currentHtml);
        this.decorateCustomToolbarButtons();
      });
    });
  };

  public render(): React.ReactNode {
    const imageDialog = this.state.showImageDialog && typeof document !== 'undefined'
      ? createPortal(
          <ImageDialog
            apiBase="/api/files"
            onSelect={this.handleDialogSelect}
            onCancel={this.handleDialogCancel}
          />,
          document.body,
        )
      : null;

    const imageResizeDialog = (
      <ImageResizeDialog
        visible={this.state.showImageResizeDialog}
        initialWidth={this.state.resizeInitialWidth}
        initialHeight={this.state.resizeInitialHeight}
        initialIsPreview={this.state.resizeInitialIsPreview}
        initialAlt={this.state.resizeInitialAlt}
        onApply={this.applyImageResize}
        onCancel={() => this.setState({ showImageResizeDialog: false })}
      />
    );

    return (
      <div id='quillarea-wrapper' role="quillarea-wrapper">
        {imageDialog}
        {imageResizeDialog}
        {!this.state.quillLoaded && (
          <div style={{ padding: 8, color: '#999', fontSize: '0.85em' }}>Lade Editor...</div>
        )}
        <div ref={this.containerRef} style={{ minHeight: 250, background: '#fff', color: '#111' }} />        
      </div>
    );
  }
}
